import React, { useCallback, useMemo, useState, useEffect } from "react";
import {faChevronRight, faCopy} from "@fortawesome/pro-regular-svg-icons";
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import usePreventNavigationContext from "@/services/context/prohibit-nagivation/use-prevent-navigation-context.ts";
import Button from "../../../../../../components/buttons/button";
import ProjectLockStatus from "../../../../../../models/enums/project-lock-status.ts";
import { useProjectFormHeadingLayoutEffect } from "../../../../context/project-heading/use-project-heading-context.ts";
import useProjectContext from "../../../../../../services/context/project/use-project-context.ts";
import useProjectFilePlanContext from "./context/use-project-file-plan-context.ts";
import WarningDialog from "../../../../../../components/dialogs/warning-dialog";
import CopyMeasuresCard from "@/pages/takeoff/layouts/copy/layouts/plan-details/components/copy-measures-card.tsx";
import ViewerLeft from "@/pages/takeoff/layouts/copy/layouts/plan-details/components/viewer-container-left.tsx";
import ViewerRight from "@/pages/takeoff/layouts/copy/layouts/plan-details/components/viewer-container-right.tsx";
import {ProjectHeadingState} from "@/pages/takeoff/context/project-heading/project-heading-context-props.ts";
import LoadingButton from "@/components/buttons/loading-button";

const ProjectCopyPlanDetails: React.FC = () => {
    const { projectLockStatus } = useProjectContext();
    
        const { isDirty, isSaving, buildingLeft: building, storeyLeft: storey, fileLeft, saveCurrentFileData } = useProjectFilePlanContext();

        const navigate = useNavigate();
        const { setIsNavigationPrevented, setSaveCallback } = usePreventNavigationContext();

        useEffect(() => {
            setIsNavigationPrevented(isDirty || isSaving);
            return () => setIsNavigationPrevented(false);
        }, [isDirty, isSaving, setIsNavigationPrevented]);

        useEffect(() => {
            setSaveCallback(saveCurrentFileData);
            return () => {
                setSaveCallback(undefined);
            };
        }, [saveCurrentFileData, setSaveCallback]);

        // Gestion Warning copyMeasures
        const [showWarningCopyMeasures, setShowWarningCopyMeasures] = useState(false);

        const handleCancelWarningCopyMeasures = useCallback(() => setShowWarningCopyMeasures(false), []);
        
        const handleConfirmWarningCopyMeasures = useCallback(() => {
            setShowWarningCopyMeasures(false);
            saveCurrentFileData();
        }, [saveCurrentFileData]);
    
        const heading = useMemo<ProjectHeadingState>(
            () => ({
                title:
                    <div className='flex flex-row gap-x-1.5 items-center'>
                        <span className='font-semibold'>Superposition</span>
                        <span>:</span>
                        <span>{building?.name}</span>
                        <FontAwesomeIcon icon={faChevronRight} className='text-sm text-gray-600'/>
                        <span>{storey?.name}</span>
                        <FontAwesomeIcon icon={faChevronRight} className='text-sm text-gray-600'/>
                        <span className='text-gray-500'>{fileLeft?.name}</span>
                    </div>,
                left:
                    <div className='flex flex-row gap-x-2'>
                        <Button variant='simple' className='pointer-events-auto flex flex-row gap-y-2 cursor-pointer' disabled={isSaving} onClick={() => navigate(-1)}>
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </Button>

                        {projectLockStatus === ProjectLockStatus.LOCK_OWNED &&
                            <LoadingButton
                                onClick={() => setShowWarningCopyMeasures(true)}
                                isLoading={isSaving} disabled={!isDirty}
                                size="sm"
                                body={
                                    <div className='flex flex-row gap-x-1'>
                                        <FontAwesomeIcon icon={faCopy} className='text-sm' />
                                        <span className='text-xs align-middle'>Copier</span>
                                    </div>
                                }
                            />
                        }
                    </div>
            }), [isSaving, isDirty, building?.name, storey?.name, fileLeft?.name, navigate, projectLockStatus]);
    
        useProjectFormHeadingLayoutEffect(heading);
    
        return (
            <div className="relative w-full h-full">
                <div className='absolute top-8 justify-center flex w-full z-2'>
                    <FontAwesomeIcon icon={faArrowLeft} className='text-5xl text-indigo-700'/>
                </div>
                <div className='absolute top-4 right-4 z-2'>
                    <CopyMeasuresCard />
                </div>

                <div className='grid grid-cols-2 h-full divide-x-2 divide-gray-300'>
                    <div className="w-full h-full bg-[#e4ede9]">
                        <ViewerLeft />
                    </div>
                    
                    <div className="w-full h-full bg-[#e4ede9]">
                        <ViewerRight />
                    </div>
                </div>

                <WarningDialog title={"Confirmer l'action"} message='Les données actuelles seront écrasées par les nouvelles.'
                open={showWarningCopyMeasures} onCancel={handleCancelWarningCopyMeasures} onConfirm={handleConfirmWarningCopyMeasures} />
            </div>
        );
}

export default ProjectCopyPlanDetails;